<template>
  <div class="product-form">
    <form @submit.prevent="handleSubmit">
      <!-- Ricerca catalogo (solo per pesti e ferti) -->
      <div v-if="hasCatalogSearch" class="catalog-search">
        <label class="catalog-search-label">{{ t('productForm.catalogSearchLabel') }}</label>
        <div class="catalog-search-row">
          <input
            v-model="catalogQuery"
            class="catalog-search-input"
            type="text"
            :placeholder="props.productType === 'pesti' ? t('productForm.catalogSearchPlaceholderPesti') : t('productForm.catalogSearchPlaceholderFerti')"
            @input="onCatalogInput"
          />
          <span v-if="catalogLoading" class="catalog-loading">⏳</span>
        </div>
        <ul v-if="catalogResults.length > 0" class="catalog-dropdown">
          <li
            v-for="item in catalogResults"
            :key="item.id"
            class="catalog-item"
            @click="selectCatalogItem(item)"
          >
            <span class="catalog-item-name">
              {{ item.id }} — {{ item.name }}
            </span>
            <span class="catalog-item-sub">
              {{ item.company }}
              <template v-if="props.productType === 'ferti' && item.n != null"> · {{ item.n }}-{{ item.p }}-{{ item.k }}</template>
              <template v-else-if="props.productType === 'pesti'"> · {{ item.stato_amministrativo }}<template v-if="item.scadenza_autorizzazione"> · {{ t('productForm.catalogExpiry') }} {{ item.scadenza_autorizzazione }}</template></template>
            </span>
          </li>
        </ul>
        <p v-if="catalogQuery.length >= 3 && !catalogLoading && catalogResults.length === 0" class="catalog-empty">
          {{ t('productForm.catalogEmpty') }}
        </p>
      </div>

      <div class="form-grid">
        <!-- Nome -->
        <AeInput
          v-model="formData.name"
          :label="t('productForm.nameLabel')"
          :placeholder="t('productForm.namePlaceholder')"
          required
          :error="errors.name"
        />

        <!-- Data ingresso -->
        <AeInput
          v-model="formData.date_enter"
          type="date"
          :label="t('productForm.dateEnterLabel')"
          required
          :error="errors.date_enter"
        />
      </div>

      <div class="form-grid">
        <!-- Quantità + UdM sempre sulla stessa riga -->
        <div class="field-qty-uom">
          <AeInput
            v-model.number="formData.quantity"
            type="number"
            min="0"
            step="any"
            :label="t('productForm.quantityLabel')"
            placeholder="0"
            required
            :error="errors.quantity"
          />
          <div class="field-uom">
            <AeSelect
              v-model="formData.uom"
              :options="uomOptions"
              :label="t('productForm.uomLabel')"
              required
            />
          </div>
        </div>

        <!-- Costo -->
        <AeInput
          v-model.number="formData.cost"
          type="number"
          min="0"
          step="any"
          :label="t('productForm.costLabel')"
          placeholder="0"
        />
      </div>

      <div class="form-grid">
        <!-- Fornitore -->
        <AeInput
          v-model="formData.supplier"
          :label="t('productForm.supplierLabel')"
          :placeholder="t('productForm.supplierPlaceholder')"
        />

        <!-- Fattura -->
        <AeInput
          v-model="formData.invoice"
          :label="t('productForm.invoiceLabel')"
          :placeholder="t('productForm.invoicePlaceholder')"
        />
      </div>

      <div class="form-grid">
        <!-- Data esaurimento -->
        <AeInput
          v-model="formData.date_end"
          type="date"
          :label="t('productForm.dateEndLabel')"
        />
        <!-- spazio vuoto per mantenere il grid 2-col -->
        <div />
      </div>

      <!-- % Rame (solo fitosanitari) -->
      <div v-if="props.productType === 'pesti'" class="form-grid">
        <AeInput
          v-model.number="formData.cu_perc"
          type="number"
          min="0"
          max="100"
          step="0.0001"
          :label="t('productForm.cuPercLabel')"
          placeholder="0"
        />
        <div />
      </div>

      <!-- Campi extra dal dbmng del product type -->
      <div v-if="hasExtraFields" class="extra-fields">
        <h4 class="extra-fields-title">{{ t('productForm.extraFieldsTitle', { name: productTypeName }) }}</h4>
        <AeForm
          :dbmng-schema="extraSchema"
          v-model="extraFormData"
          :options="{ showActions: false }"
        />
      </div>

      <!-- Ricetta (solo per tipo rations) -->
      <div v-if="hasRecipe" class="recipe-section">
        <div class="recipe-header">
          <h4 class="recipe-title">{{ t('productForm.recipeTitle') }}</h4>
          <button type="button" class="add-row-btn" @click="addRecipeRow">{{ t('productForm.addIngredient') }}</button>
        </div>

        <div v-if="recipeRows.length === 0" class="recipe-empty">
          {{ t('productForm.recipeEmpty') }}
        </div>

        <table v-else class="recipe-table">
          <thead>
            <tr>
              <th>{{ t('productForm.tableProduct') }}</th>
              <th class="col-qty">{{ t('productForm.tableQuantity') }}</th>
              <th class="col-note">{{ t('productForm.tableNote') }}</th>
              <th class="col-remove"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in recipeRows" :key="row.id_rations_recipe">
              <td>
                <select v-model="row.id_product" class="form-control">
                  <option value="">{{ t('productForm.selectPlaceholder') }}</option>
                  <option v-for="p in availableProducts" :key="p.id_product" :value="p.id_product">
                    {{ p.name }}{{ p.date_end && p.date_end <= _today ? t('productForm.exhausted') : '' }}
                  </option>
                </select>
              </td>
              <td class="col-qty">
                <input v-model.number="row.quantity" type="number" step="0.01" min="0" class="form-control" />
              </td>
              <td class="col-note">
                <input v-model="row.note" type="text" class="form-control" :placeholder="t('productForm.notePlaceholderRow')" />
              </td>
              <td class="col-remove">
                <button type="button" class="remove-row-btn" @click="removeRecipeRow(i)">&times;</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Note -->
      <AeTextarea
        v-model="formData.note"
        :label="t('productForm.noteLabel')"
        :placeholder="t('productForm.notePlaceholder')"
        :rows="2"
      />

      <!-- Immagini -->
      <ImageLoader
        type="product"
        :record="draftId"
        v-model="images"
      />

      <!-- Errori -->
      <div v-if="validationErrors.length > 0" class="validation-errors">
        <p v-for="(error, index) in validationErrors" :key="index" class="error">
          {{ error }}
        </p>
      </div>

      <!-- Azioni (nascoste se il parent gestisce il footer della modal) -->
      <div v-if="!hideActions" class="form-actions">
        <AeButton variant="secondary" type="button" @click="$emit('cancel')">
          {{ t('productForm.cancel') }}
        </AeButton>
        <AeButton variant="primary" type="submit" :disabled="loading">
          {{ loading ? t('productForm.saving') : (isEditMode ? t('productForm.saveChanges') : t('productForm.addProduct')) }}
        </AeButton>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePoderiStore } from '../stores/poderiStore'
import { useDbStore } from '../stores/dbStore'
import { AeInput, AeTextarea, AeButton, AeSelect, AeForm } from '@aevue/components'
import { UOM_OPTIONS, getProductType, normalizeDbmng } from '../composables/useWarehouse'
import ImageLoader from './ImageLoader.vue'
import { api } from '../services/api.js'

const { t } = useI18n()

const props = defineProps({
  productId:   { type: [String, Number], default: null },
  farmCenterId:{ type: [String, Number], required: true },
  productType: { type: String, required: true },
  hideActions: { type: Boolean, default: false },
  // Precompila formData in creazione (es. dati estratti da un messaggio vocale)
  prefill:     { type: Object, default: null },
  // Campi extra non in metadata.py, aggiunti al payload solo in creazione
  // (es. voice_source) — il backend li instrada da solo nella colonna json
  extraJson:   { type: Object, default: null },
})

const emit = defineEmits(['submit', 'cancel'])

const poderiStore = usePoderiStore()
const dbStore = useDbStore()
const loading = ref(false)
const images = ref([])
const draftId = props.productId || crypto.randomUUID()
const validationErrors = ref([])
const uomOptions = UOM_OPTIONS

const isEditMode = computed(() => !!props.productId)

// ── Ricerca catalogo ──────────────────────────────────────────────────────────
const hasCatalogSearch = computed(() => ['pesti', 'ferti'].includes(props.productType))
const catalogQuery   = ref('')
const catalogResults = ref([])
const catalogLoading = ref(false)
let _catalogTimer    = null

function onCatalogInput() {
  clearTimeout(_catalogTimer)
  catalogResults.value = []
  if (catalogQuery.value.length < 3) return
  _catalogTimer = setTimeout(fetchCatalog, 350)
}

async function fetchCatalog() {
  catalogLoading.value = true
  try {
    const res = await api.get(`/api/v2/poderi2/products/search/${props.productType}/${encodeURIComponent(catalogQuery.value)}`)
    if (res.ok) catalogResults.value = res.data || []
  } catch { /* silenzioso */ } finally {
    catalogLoading.value = false
  }
}

function selectCatalogItem(item) {
  formData.value.name = item.name
  if (props.productType === 'ferti') {
    formData.value.n = item.n ?? null
    formData.value.p = item.p ?? null
    formData.value.k = item.k ?? null
    // Link persistente al registro aepest.ferti (colonna reale poderi.product.code_product)
    // + coefficienti di disponibilità azoto pluriennale (solo per gli "effluenti di allevamento" generici)
    formData.value.code_product = item.id ?? null
    // aepest.ferti.json ha default '{}' su tutte le righe: valorizza solo
    // quando contiene davvero i coefficienti (righe "effluenti di allevamento")
    formData.value.ferti_json = item.json?.org_type ? item.json : null
  }
  if (props.productType === 'pesti') {
    formData.value.cu_perc = item.copper_perc ?? null
    // Link persistente al registro aepest.product_raw (numero_registrazione)
    formData.value.code_product = item.id ?? null
  }
  catalogQuery.value   = ''
  catalogResults.value = []
}
// ─────────────────────────────────────────────────────────────────────────────

const currentType = computed(() => getProductType(props.productType))
const productTypeName = computed(() => currentType.value?.name || props.productType)
const hasRecipe = computed(() => !!currentType.value?.hasRecipe)

// Normalizza dbmng da array a oggetto se necessario
const normalizedDbmng = computed(() => normalizeDbmng(currentType.value?.dbmng))

// Campi extra dal dbmng
const extraFields = computed(() => Object.keys(normalizedDbmng.value))

const hasExtraFields = computed(() => extraFields.value.length > 0)

const extraSchema = computed(() => ({
  fields: normalizedDbmng.value
}))

const extraFormData = computed({
  get() {
    const data = {}
    for (const key of extraFields.value) {
      data[key] = formData.value[key] ?? null
    }
    return data
  },
  set(val) {
    if (!val) return
    for (const [key, value] of Object.entries(val)) {
      formData.value[key] = value
    }
  }
})

// Prodotti disponibili per la ricetta: attivi (non esauriti) e non-rations
// Include sempre i prodotti già selezionati nelle righe, anche se scaduti
const _today = new Date().toISOString().substring(0, 10)
const availableProducts = computed(() => {
  const selectedIds = new Set(recipeRows.value.map(r => r.id_product).filter(Boolean))
  return poderiStore.products
    .filter(p =>
      p.id_product_type !== 'rations' &&
      ((!p.date_end || p.date_end > _today) || selectedIds.has(p.id_product))
    )
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
})

// Ricetta: array di righe
const recipeRows = ref([])

function addRecipeRow() {
  recipeRows.value.push({
    id_rations_recipe: crypto.randomUUID(),
    id_product: '',
    quantity: null,
    note: ''
  })
}

function removeRecipeRow(index) {
  recipeRows.value.splice(index, 1)
}

const today = new Date().toISOString().substring(0, 10)

const formData = ref({
  name: '',
  date_enter: today,
  date_end: null,
  quantity: null,
  uom: 'kg',
  cost: null,
  supplier: '',
  invoice: '',
  note: '',
  cu_perc: null,
  code_product: null,
  ferti_json: null,
})

const errors = ref({
  name: '',
  date_enter: '',
  quantity: ''
})

onMounted(async () => {
  if (hasRecipe.value) {
    await poderiStore.fetchProductsByFarmCenter(props.farmCenterId)
  }

  if (!isEditMode.value && props.prefill) {
    for (const [key, value] of Object.entries(props.prefill)) {
      if (value !== undefined) formData.value[key] = value
    }
  }

  if (isEditMode.value) {
    loading.value = true
    try {
      const result = await dbStore.selectOne('product', props.productId)
      if (result.ok && result.data) {
        const item = result.data
        formData.value = {
          name: item.name || '',
          date_enter: item.date_enter || today,
          date_end: item.date_end || null,
          quantity: item.quantity,
          uom: item.uom || 'kg',
          cost: item.cost || null,
          supplier: item.supplier || '',
          invoice: item.invoice || '',
          note: item.note || ''
        }
        formData.value.cu_perc = item.cu_perc ?? null
        formData.value.code_product = item.code_product ?? null
        formData.value.ferti_json = item.ferti_json ?? null
        // Carica campi extra
        for (const key of extraFields.value) {
          formData.value[key] = item[key] ?? null
        }
        // Carica ricetta
        if (hasRecipe.value && item.recipe) {
          try {
            recipeRows.value = typeof item.recipe === 'string'
              ? JSON.parse(item.recipe)
              : item.recipe
          } catch {
            recipeRows.value = []
          }
        }
        // Carica immagini
        images.value = Array.isArray(item.images) ? item.images : []
      }
    } catch (e) {
      console.error('Error loading product:', e)
      validationErrors.value = [t('productForm.errorLoading')]
    } finally {
      loading.value = false
    }
  }
})

function validateForm() {
  validationErrors.value = []
  errors.value = { name: '', date_enter: '', quantity: '' }
  let valid = true

  if (!formData.value.name?.trim()) {
    errors.value.name = t('productForm.errorNameRequired')
    valid = false
  }
  if (!formData.value.date_enter) {
    errors.value.date_enter = t('productForm.errorDateRequired')
    valid = false
  }
  if (formData.value.quantity == null || formData.value.quantity < 0) {
    errors.value.quantity = t('productForm.errorQuantityRequired')
    valid = false
  }
  return valid
}

defineExpose({ submit: () => handleSubmit(), isLoading: loading, isEditMode })

async function handleSubmit() {
  if (!validateForm()) return

  loading.value = true
  try {
    const data = {
      id_product: draftId,
      name: formData.value.name.trim(),
      date_enter: formData.value.date_enter,
      date_end: formData.value.date_end || null,
      quantity: formData.value.quantity,
      uom: formData.value.uom,
      cost: formData.value.cost,
      supplier: formData.value.supplier?.trim() || '',
      invoice: formData.value.invoice?.trim() || '',
      note: formData.value.note?.trim() || '',
      id_farm_center: props.farmCenterId,
      id_product_type: props.productType,
      images: images.value
    }

    if (props.productType === 'pesti') {
      data.cu_perc = formData.value.cu_perc ?? null
      data.code_product = formData.value.code_product ?? null
    }
    if (props.productType === 'ferti') {
      data.code_product = formData.value.code_product ?? null
      data.ferti_json = formData.value.ferti_json ?? null
    }
    // Aggiungi campi extra
    for (const key of extraFields.value) {
      data[key] = formData.value[key]
    }

    // Aggiungi ricetta
    if (hasRecipe.value) {
      data.recipe = JSON.stringify(recipeRows.value)
    }

    if (!isEditMode.value && props.extraJson) {
      Object.assign(data, props.extraJson)
    }

    let result
    if (isEditMode.value) {
      result = await poderiStore.updateProduct(props.productId, data)
    } else {
      result = await poderiStore.createProduct(data)
    }

    if (result.ok) {
      emit('submit', result.data)
    } else {
      validationErrors.value = [result.message || t('productForm.errorSaving')]
    }
  } catch (e) {
    console.error('Error saving product:', e)
    validationErrors.value = [e.message || t('productForm.errorSaving')]
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.product-form form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.field-qty-uom {
  display: flex;
  gap: 0.75rem;
  align-items: flex-end;
}

.field-qty-uom > :first-child {
  flex: 1;
}

.field-uom {
  width: 120px;
  flex-shrink: 0;
}

@media (max-width: 600px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}

.extra-fields {
  border-top: 1px solid #eee;
  padding-top: 0.75rem;
}

.extra-fields-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #555;
  margin: 0 0 0.5rem;
}

/* Ricetta */
.recipe-section {
  border-top: 1px solid #eee;
  padding-top: 0.75rem;
}

.recipe-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.recipe-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #555;
  margin: 0;
}

.add-row-btn {
  background: none;
  border: 1px solid var(--ae-primary, #4a90d9);
  color: var(--ae-primary, #4a90d9);
  border-radius: 4px;
  font-size: 0.8rem;
  padding: 0.2rem 0.6rem;
  cursor: pointer;
}

.add-row-btn:hover {
  background: rgba(74, 144, 217, 0.1);
}

.recipe-empty {
  font-size: 0.85rem;
  color: #999;
  padding: 0.5rem 0;
}

.recipe-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.recipe-table th {
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  color: #718096;
  padding: 0.25rem 0.4rem;
  border-bottom: 1px solid #e2e8f0;
}

.recipe-table td {
  padding: 0.3rem 0.4rem;
  vertical-align: middle;
}

.recipe-table .col-qty {
  width: 90px;
}

.recipe-table .col-note {
  width: 30%;
}

.recipe-table .col-remove {
  width: 32px;
  text-align: center;
}

.form-control {
  width: 100%;
  padding: 0.35rem 0.5rem;
  border: 1px solid #cbd5e0;
  border-radius: 5px;
  font-size: 0.85rem;
  font-family: inherit;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: #4299e1;
}

.remove-row-btn {
  background: none;
  border: none;
  color: #e53e3e;
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0 0.2rem;
  line-height: 1;
}

.remove-row-btn:hover {
  color: #c53030;
}

/* ── Ricerca catalogo ── */
.catalog-search {
  border: 1px solid var(--border-color, rgba(45,55,72,0.15));
  border-radius: 8px;
  padding: 0.65rem 0.75rem 0.5rem;
  background: var(--ae-surface, rgba(240,240,245,0.4));
  position: relative;
}
.catalog-search-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted, #718096);
  margin-bottom: 0.35rem;
}
.catalog-search-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.catalog-search-input {
  flex: 1;
  border: 1px solid var(--border-color, #cbd5e0);
  border-radius: 6px;
  padding: 0.35rem 0.6rem;
  font-size: 0.9rem;
  font-family: inherit;
  background: var(#fff);
  color: var(--text-color, #2d3748);
}
.catalog-search-input:focus {
  outline: none;
  border-color: var(--color-primary, #4a90d9);
}
.catalog-loading {
  font-size: 0.85rem;
}
.catalog-dropdown {
  list-style: none;
  margin: 0.35rem 0 0;
  padding: 0;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 6px;
  background: #fff;
  max-height: 220px;
  overflow-y: auto;
  position: absolute;
  left: 0; right: 0;
  z-index: 100;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
}
.catalog-item {
  padding: 0.45rem 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid var(--border-color, #f0f0f0);
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.catalog-item:last-child { border-bottom: none; }
.catalog-item:hover { background: rgba(74,144,217,0.08); }
.catalog-item-name {
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--text-color, #2d3748);
}
.catalog-item-sub {
  font-size: 0.75rem;
  color: var(--text-muted, #718096);
}
.catalog-empty {
  font-size: 0.8rem;
  color: var(--text-muted, #718096);
  margin: 0.4rem 0 0;
  font-style: italic;
}

.validation-errors {
  background: #fee;
  border: 1px solid #fcc;
  border-radius: 6px;
  padding: 0.75rem;
}

.error {
  color: var(--danger-color, #e53e3e);
  font-size: 0.8rem;
  margin: 0.25rem 0;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 0.75rem;
  border-top: 1px solid #eee;
}
</style>
